import React, { useEffect, useRef, useState } from 'react';

import { useToast } from '~libs/toast';

import ClickableIcon from '~components/ClickableIcon';
import Icon, { faCheck, faEnvelope, faFilePdf } from '~components/icon'; //faEye
import Spinner from '~components/Spinner';
import { Row } from '~components/table-view-v3';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

interface ComponentProps {
  row?: Row;
}

const StatementActions: React.FC<ComponentProps> = ({ row }) => {
  const { generateSeparate, emailSeparate, isGenerating } = useDocumentBatchGeneration();
  const [jobCreated, setJobCreated] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'pdf' | 'email' | null>(null);
  const { showToast } = useToast();
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // const handleView = () => {
  //   // TODO: Implement view functionality
  //   return;
  // };

  const handlePdf = async () => {
    if (!row?.id) return;
    setActionType('pdf');
    try {
      await generateSeparate('sah-statement', [row.id as string]);
      setJobCreated(true);
      showToast('success', 'Statement generation started');
    } catch (error) {
      setJobCreated(false);
      setActionType(null);
      const message = error instanceof Error ? error.message : 'Failed to start statement generation';
      showToast('danger', message);
      // Optional: keep error visible in console for diagnostics
      console.error('Statement PDF generation failed:', error);
    }
  };

  const handleEmail = async () => {
    if (!row?.id) return;
    setActionType('email');
    try {
      await emailSeparate('sah-statement', [row.id as string]);
      setJobCreated(true);
      showToast('success', 'Statement email scheduled');
    } catch (error) {
      setJobCreated(false);
      setActionType(null);
      const message = error instanceof Error ? error.message : 'Failed to schedule statement email';
      showToast('danger', message);
      console.error('Statement email scheduling failed:', error);
    }
  };

  // Auto-reset jobCreated after a short delay to allow retries
  useEffect(() => {
    if (jobCreated) {
      resetTimerRef.current = setTimeout(() => {
        setJobCreated(false);
        setActionType(null);
      }, 3000);
    }
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, [jobCreated]);

  return (
    <div className="d-flex align-items-center">
      {/* <ClickableIcon id="view-statement-icon" icon={faEye} toolTip="View Statement" onClick={handleView} /> */}
      {(() => {
        if (isGenerating && actionType === 'pdf') {
          return (
            <Spinner type="clip" size={16} loading={true} className="ms-2 me-2" dataCy="statement-generate-spinner" />
          );
        }
        if (jobCreated && actionType === 'pdf') {
          return (
            <Icon
              icon={faCheck}
              className="text-success ms-2 me-2"
              title="Statement generation started"
              style={{ cursor: 'default' }}
            />
          );
        }
        return (
          <ClickableIcon
            id="generate-statement-pdf-icon"
            icon={faFilePdf}
            toolTip="Generate PDF"
            onClick={handlePdf}
            disabled={isGenerating}
          />
        );
      })()}
      {(() => {
        if (isGenerating && actionType === 'email') {
          return (
            <Spinner type="clip" size={16} loading={true} className="ms-2 me-2" dataCy="statement-email-spinner" />
          );
        }
        if (jobCreated && actionType === 'email') {
          return (
            <Icon
              icon={faCheck}
              className="text-success ms-2 me-2"
              title="Statement email scheduled"
              style={{ cursor: 'default' }}
            />
          );
        }
        return (
          <ClickableIcon
            id="email-statement-icon"
            icon={faEnvelope}
            toolTip="Email Statement"
            onClick={handleEmail}
            disabled={isGenerating}
          />
        );
      })()}
    </div>
  );
};

export default StatementActions;
